import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import { Children, createContext, useContext } from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Text, type TextProps } from "../../components/ui/text";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * The dialog nav pane: the navigation column of a manage dialog — a fixed
 * identity header over a scrolling list of grouped rows. It paints the
 * `sidebar` surface in light and the `popover` rung in dark, so it always
 * lifts above the popup's `background` fill whatever the shell's dark
 * navigation setting does to `--sidebar`.
 *
 * A third nav genre, deliberately separate from the shell `Sidebar*` family
 * (collapse-to-icon, mobile drawer, tooltips) and the `SidebarPane` particle
 * (resize seam, route-swapped headers): inside a dialog none of that
 * machinery exists, and this pane's metrics evolve independently of the
 * shell's. Visual kinship is carried by the shared tokens instead — the
 * `sidebar` surface family and the one `wash-*` ramp.
 *
 * ## The 8px gutter and the 24px glyph line
 *
 * ```
 *   │← 8 →│← 8 →│ 16px glyph │
 *   gutter  row's own px      └─ glyph center = 8 + 8 + 8 = 24
 * ```
 *
 * The gutter lives on the header and the content column; a row's own padding
 * carries its glyph to the 16px reading edge. The header's 32px identity tile
 * sits directly on the gutter, so its center lands on the same 24px line the
 * row glyphs use — that is the whole alignment contract, and both numbers are
 * stated here once.
 *
 * ## Collapsed: the 48px icon rail
 *
 * `collapsed` keeps the glyph line and drops everything beside it: the pane is
 * gutter + 32px row + gutter, each row a 32px square with its glyph centered
 * on the same 24px line, and the title, group labels, row labels and badges
 * leave the paint but stay in the accessibility tree — a row's name is still
 * its label. It is the phone treatment of a manage dialog, where a 176px nav
 * beside a content column leaves neither enough width.
 */
const NavPaneCollapsedContext = createContext(false);

/** Gutter + 32px row + gutter. */
const COLLAPSED_WIDTH = 48;

/** The expanded pane: labels at full length beside a content column with room
 *  left to work in. A host on a narrower viewport may trade some of it back
 *  (`width`), down to about 200 before the longer row labels start to clip. */
const DEFAULT_WIDTH = 240;

export type NavPaneProps = BoxProps &
  Omittable<{
    /** Icon-only: the 48px rail. Labels stay as accessible names. */
    collapsed?: boolean;
  }>;

export function NavPane({
  children,
  className,
  collapsed = false,
  width = DEFAULT_WIDTH,
  ...props
}: NavPaneProps): React.ReactElement {
  return (
    <NavPaneCollapsedContext.Provider value={collapsed}>
      <Box
        border="right"
        // The pane sits INSIDE a dialog whose popup rests on the page
        // (`background`), so in dark it reads as the lifted plane beside that
        // fill whatever the shell's navigation setting says: the dark
        // "Navigation" appearance option (recessed / lifted) moves `--sidebar`
        // either way, so the pane cannot paint it. `popover` is always high in
        // the ladder above `background`, and no appearance option moves it.
        // Light keeps the `sidebar` rung — the option is dark-only.
        className={cn(
          "dark:border-popover-border dark:bg-popover",
          className,
        )}
        data-collapsed={collapsed ? "" : undefined}
        data-slot="nav-pane"
        direction="column"
        minHeight={0}
        shrink={false}
        surface="sidebar"
        {...props}
        width={collapsed ? COLLAPSED_WIDTH : width}
      >
        {children}
      </Box>
    </NavPaneCollapsedContext.Provider>
  );
}

/**
 * The fixed identity header: a 48px bordered bar on the pane gutter. Compose
 * an `IconTile size="lg"` (32px — centered on the glyph line) with a
 * `NavPaneTitle`. The bottom hairline sits on the same 48px rung as the
 * manage dialogs' content header rule, so across the pane's right border the
 * two read as one continuous line.
 */
export function NavPaneHeader({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box
      align="center"
      border="bottom"
      data-slot="nav-pane-header"
      direction="row"
      gap="sm"
      height={48}
      paddingX="sm"
      shrink={false}
    >
      {children}
    </Box>
  );
}

/** The pane's title — the identity type rung (16px semibold). Collapsed, it
 *  leaves the paint and stays readable to assistive tech. */
export function NavPaneTitle({
  children,
  className,
  ...props
}: TextProps): React.ReactElement {
  const collapsed = useContext(NavPaneCollapsedContext);
  return (
    <Text
      className={cn(collapsed && "sr-only", className)}
      size="base"
      truncate
      weight="semibold"
      {...props}
    >
      {children}
    </Text>
  );
}

/**
 * The scrolling group list. Full-bleed ScrollArea so the scrollbar rides the
 * pane edge; the gutter sits on the content column inside it.
 */
export function NavPaneContent({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box direction="column" grow minHeight={0} minWidth={0}>
      <ScrollArea scrollFade>
        <Box data-slot="nav-pane-content" paddingBottom="sm" paddingX="sm">
          {children}
        </Box>
      </ScrollArea>
    </Box>
  );
}

/** One nav section: a tight column of rows, with vertical rhythm between
 *  sections. Horizontal inset comes from the content column's gutter. */
export function NavPaneGroup({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box data-slot="nav-pane-group" gap="2xs" paddingTop="sm">
      {children}
    </Box>
  );
}

/**
 * The section label: a 32px block whose own padding puts the text on the
 * rows' 16px reading edge. CSS capitalizes each word, so a call site passes
 * the plain phrase and never shouts in caps.
 */
export function NavPaneGroupLabel({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  const collapsed = useContext(NavPaneCollapsedContext);
  // No 32px block on the rail — the label keeps only its place in the
  // accessibility tree.
  if (collapsed) {
    return (
      <Text visuallyHidden data-slot="nav-pane-group-label">
        {children}
      </Text>
    );
  }
  return (
    <Box
      align="center"
      data-slot="nav-pane-group-label"
      direction="row"
      height={32}
      paddingX="sm"
      shrink={false}
    >
      <Text
        className="text-muted-foreground capitalize"
        size="2xs"
        weight="semibold"
      >
        {children}
      </Text>
    </Box>
  );
}

/**
 * A hairline between two groups: the boundary that reads as "a different kind
 * of thing below", not just more of the same list. Groups carry their own
 * vertical rhythm, so reach for this only when the sections are genuinely
 * unrelated. Spans the pane's content column — the gutter is the only inset,
 * matching `SidebarSeparator`.
 */
export function NavPaneSeparator(): React.ReactElement {
  return (
    <Box paddingY="sm">
      <Separator data-slot="nav-pane-separator" />
    </Box>
  );
}

/**
 * A group with nothing in it yet: the row geometry and reading edge of a real
 * row, on the faint ink rung, and inert — nothing here is pressable, so it is
 * not a disabled `NavPaneItem`.
 */
export function NavPaneEmpty({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  const collapsed = useContext(NavPaneCollapsedContext);
  if (collapsed) {
    return (
      <Text visuallyHidden data-slot="nav-pane-empty">
        {children}
      </Text>
    );
  }
  return (
    <Box
      align="center"
      data-slot="nav-pane-empty"
      direction="row"
      minHeight={32}
      paddingX="sm"
      paddingY="2xs"
    >
      <Text size="sm" variant="faint">
        {children}
      </Text>
    </Box>
  );
}

export type NavPaneItemProps = useRender.ComponentProps<"button"> &
  Omittable<{
    /** The selected row: the selected wash plus the full-ink lift. Also emits
     *  `aria-current="page"` — the pane is a navigation surface. */
    isActive?: boolean;
  }>;

/**
 * One nav row: 32px, squared, a 16px glyph on the glyph line and a 12px
 * glyph↔label gap. State is the shared wash ramp (hover → active →
 * selected), never a solid control fill, and the glyph carries the sidebar
 * three-rung ink ramp — rest a step under the muted label, lifted on hover,
 * full foreground when selected. Explicitly-colored glyphs (`text-c`) are
 * left alone.
 *
 * On a collapsed pane the row is a 32px square: the glyph centers on the
 * glyph line and every other child — label, badge — goes screen-reader-only,
 * so the button's accessible name is still its label. Bare text children are
 * wrapped so the rule can reach them.
 */
export function NavPaneItem({
  isActive = false,
  className,
  render,
  children,
  ...props
}: NavPaneItemProps): React.ReactElement {
  const collapsed = useContext(NavPaneCollapsedContext);
  const defaultProps = {
    "aria-current": isActive ? ("page" as const) : undefined,
    className: cn(
      "flex h-8 w-full min-w-0 shrink-0 items-center gap-3 rounded-md px-2 text-left text-sidebar-foreground text-sm outline-hidden inset-ring-ring hover:bg-wash-hover focus-visible:inset-ring-2 active:bg-wash-active disabled:pointer-events-none disabled:opacity-64 data-[active=true]:bg-wash-selected data-[active=true]:text-sidebar-foreground data-[active=true]:hover:bg-wash-selected-hover [&>svg]:shrink-0 [&>svg:not([class*='size-'])]:size-4",
      "[&>svg:not([class^='text-']):not([class*='_text-'])]:text-muted-foreground not-data-[active=true]:hover:[&>svg:not([class^='text-']):not([class*='_text-'])]:text-muted-foreground data-[active=true]:[&>svg:not([class^='text-']):not([class*='_text-'])]:text-sidebar-foreground",
      collapsed && "w-8 justify-center px-0 [&>*:not(svg)]:sr-only",
      className,
    ),
    "data-active": isActive,
    "data-slot": "nav-pane-item",
    type: "button" as const,
    children: collapsed
      ? Children.map(children, (child) =>
          typeof child === "string" || typeof child === "number" ? (
            <Text as="span">{child}</Text>
          ) : (
            child
          ),
        )
      : children,
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(defaultProps, props),
    render,
  });
}

/** Trailing slot on a row — a count, a status dot. Pins to the far edge and
 *  stays out of the glyph/label flow. */
export function NavPaneBadge({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box
      align="center"
      className="ms-auto"
      data-slot="nav-pane-badge"
      direction="row"
      shrink={false}
    >
      {children}
    </Box>
  );
}
